import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import FeatureGates from '@atlaskit/feature-gate-js-client';
/**
 * Custom hook to track the viewed status of a component.
 */

function useViewedTimer() {
  var timerRef = useRef([]);
  var fired = useRef(false);
  var shouldFireViewedEvent = function shouldFireViewedEvent() {
    if (fired.current || timerRef.current.length === 0) {
      return false;
    }
    return true;
  };
  var startTimer = useCallback(function () {
    timerRef.current.push({
      start: Date.now(),
      end: null
    });
  }, []);
  var stopTimer = useCallback(function () {
    if (timerRef.current.length > 0) {
      timerRef.current = timerRef.current.map(function (tracker) {
        return _objectSpread(_objectSpread({}, tracker), {}, {
          end: tracker.end || Date.now()
        });
      });
    }
  }, []);
  var calculateDuration = useCallback(function () {
    return timerRef.current.reduce(function (acc, tracker) {
      if (tracker.end) {
        return acc + tracker.end - tracker.start;
      }
      return acc + Date.now() - tracker.start;
    }, 0);
  }, []);
  var clearTimers = useCallback(function () {
    timerRef.current = [];
  }, []);
  var setFired = function setFired(value) {
    fired.current = value;
  };
  return {
    shouldFireViewedEvent: shouldFireViewedEvent,
    startTimer: startTimer,
    stopTimer: stopTimer,
    calculateDuration: calculateDuration,
    clearTimers: clearTimers,
    setFired: setFired
  };
}
/**
 * Component that can be used to track the viewed status (and duration) of a component.
 * Fires a `{component} viewed` event when:
 * - The `hasUserInteractedProp` goes from `false` to `true`
 * - OR
 * - The component is unmounted
 */
export default function ViewedTracker(_ref) {
  var createFireAnalyticsEvent = _ref.createFireAnalyticsEvent,
    hasUserInteracted = _ref.hasUserInteracted;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    viewed = _useState2[0],
    setViewed = _useState2[1];
  var _useViewedTimer = useViewedTimer(),
    shouldFireViewedEvent = _useViewedTimer.shouldFireViewedEvent,
    startTimer = _useViewedTimer.startTimer,
    stopTimer = _useViewedTimer.stopTimer,
    calculateDuration = _useViewedTimer.calculateDuration,
    clearTimers = _useViewedTimer.clearTimers,
    setFired = _useViewedTimer.setFired;
  var fireViewed = React.useCallback(function (firer) {
    // if we have already fired, or the notification has not been scrolled into view yet then we don't send a `viewed` event
    if (!shouldFireViewedEvent()) {
      return;
    }
    var viewedDuration = calculateDuration();
    firer(viewedDuration);
    setFired(true);
    // clear out the timers
    clearTimers();
    setViewed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var callAnalyticEvent = React.useCallback(function () {
    return fireViewed(function (viewedDuration) {
      createFireAnalyticsEvent(viewedDuration);
      if (hasUserInteracted) {
        // Fire Calibration flag for Notify First Page View V2 Experiment

        FeatureGates.getExperimentValue('cc_onboarding_notify_first_page_view_v2_aa', 'cohort', 'not-enrolled', {
          fireExperimentExposure: true
        });
      }
    });
  },
  // if this callback CHANGES we will fire our `viewed` event
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hasUserInteracted]);
  React.useEffect(function () {
    var fireViewedOnHidden = function fireViewedOnHidden() {
      if (document.visibilityState === 'hidden') {
        callAnalyticEvent();
      }
    };
    var handleBeforeUnload = function handleBeforeUnload() {
      callAnalyticEvent();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', fireViewedOnHidden);
    return function () {
      callAnalyticEvent();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', fireViewedOnHidden);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callAnalyticEvent]);
  if (viewed) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Waypoint, {
    key: "component-viewed",
    onEnter: startTimer,
    onLeave: stopTimer
  }, /*#__PURE__*/React.createElement("span", null));
}